import { Badge, Button, Drawer, Dropdown, Tooltip } from 'antd'
import Icon, {
  ArrowLeftOutlined,
  BellOutlined,
  BookOutlined,
  CaretDownFilled,
  FolderFilled,
  GiftOutlined,
  HomeFilled,
  MailOutlined,
  MenuOutlined,
  MobileFilled,
  ProfileOutlined,
  QuestionCircleOutlined,
  SettingFilled,
  UserOutlined,
} from '@ant-design/icons'
import React, { useState } from 'react'
import { ReactComponent as AnchorIcon } from 'assets/icons/anchor.svg'

import { useLocation, useNavigate } from 'react-router-dom'
import cargomateLogo from 'assets/cargomate-logo.svg'
import colors from 'global/styles/colors'
import { connect } from 'react-redux'
import deviceTypes from 'constants/deviceTypes'
import EnableNotificationsPrompt from './components/EnableNotificationsPrompt'
import featureCodes from 'constants/featureCodes'
import Feedback from './components/Feedback'
import { Menu } from 'antd'
import MenuContent from './components/MenuContent'
import { MODULES } from 'constants/modules'
import { openFeedbackModal } from 'domain/User/actions'
import paths from 'constants/paths'
import propTypes from 'global/propTypes'
import PropTypes from 'prop-types'
import { selectors } from 'domain'
import useDeviceType from 'hooks/useDeviceType'
import useFeatureAccessControlEnabled from 'hooks/useFeatureAccessControlEnabled'
import userRoles from 'constants/userRoles'
import { useTranslation } from 'react-i18next'
import WhatsNewDropdown from './components/WhatsNewDropdown'

import './navbar.less'

const Navbar = ({
  closeWhatsNewNotification,
  hasUnseenNews,
  isUserSSO,
  onCancelEnableNotificationsPrompt,
  onConfirmEnableNotificationsPrompt,
  onNotificationsButtonClicked,
  openFeedbackModal,
  setNewsIdsSeen,
  shouldShowEnableNotificationsPrompt,
  shouldShowPortCallNotifications,
  shouldShowWhatsNewNotification,
  signedInAs,
  signOut,
  totalNoOfNewNotifications,
  userImoNumber,
  userModule,
  userRole,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [drawerVisible, setDrawerVisible] = useState(false)

  const onMenuItemSelected = item => {
    navigate(item.key)
    setDrawerVisible(false)
  }

  const goToHomePage = () =>
    userRole === userRoles.SHIP
      ? navigate(`${paths.ship.root}/${userImoNumber}`)
      : navigate(paths.overview)

  // const isUserHL = cognitoGroup
  //   ? includes(cognitoGroup, userCognitoGroups.HAPAG) ||
  //   includes(cognitoGroup, userCognitoGroups.HL)
  //   : false

  // Sub-menu items for non-ship users
  const nonShipSubMenuItems = [
    {
      icon: <HomeFilled className="navbar__menu-item-icon" />,
      key: paths.overview,
      label: t('navbar.overview'),
    },
    {
      icon: <FolderFilled className="navbar__menu-item-icon" />,
      key: paths.fleet,
      label: t('navbar.fleet'),
    },
    {
      icon: <Icon className="navbar__menu-item-icon" component={AnchorIcon} />,
      key: paths.ports.root,
      label: t('navbar.ports'),
    },
    {
      icon: <BookOutlined className="navbar__menu-item-icon" />,
      key: paths.manual,
      label: t('navbar.userManual'),
    },
    {
      icon: <QuestionCircleOutlined className="navbar__menu-item-icon" />,
      key: paths.help,
      label: t('navbar.help'),
    },
    {
      icon: <ProfileOutlined className="navbar__menu-item-icon" />,
      key: paths.viewReleaseNotes,
      label: t('navbar.releaseNotes'),
    },
    {
      type: 'divider',
    },
    {
      disabled: true,
      key: 'version',
      label: `v${WEB_DASHBOARD_VERSION}`,
      style: { cursor: 'default' },
    },
  ]

  // Sub-menu items for ship users
  const shipSubMenuItems = [
    {
      icon: <HomeFilled className="navbar__menu-item-icon" />,
      key: `${paths.ship.root}/${userImoNumber}`,
      label: t('navbar.overview'),
    },
    {
      icon: <BookOutlined className="navbar__menu-item-icon" />,
      key: paths.manual,
      label: t('navbar.userManual'),
    },
    {
      icon: <QuestionCircleOutlined className="navbar__menu-item-icon" />,
      key: paths.help,
      label: t('navbar.help'),
    },
    {
      icon: <ProfileOutlined className="navbar__menu-item-icon" />,
      key: paths.viewReleaseNotes,
      label: t('navbar.releaseNotes'),
    },
    {
      type: 'divider',
    },
    {
      disabled: true,
      key: 'version',
      label: `v${WEB_DASHBOARD_VERSION}`,
      style: { cursor: 'default' },
    },
  ]

  // Sub-menu items for ROB module users
  const robSubMenuItems = [
    {
      icon: <HomeFilled className="navbar__menu-item-icon" />,
      key: paths.rob,
      label: t('navbar.rob.home'),
    },
  ]

  // Sub-menu item for admin users
  const adminSubmenu = [
    {
      icon: <HomeFilled className="navbar__menu-item-icon" />,
      key: paths.admin,
      label: t('navbar.admin.vessels'),
    },
    {
      icon: <MobileFilled className="navbar__menu-item-icon" />,
      key: paths.deviceManagement,
      label: t('navbar.admin.deviceManagement'),
    },
    {
      icon: <SettingFilled className="navbar__menu-item-icon" />,
      key: paths.adminFeatureToggle,
      label: t('navbar.admin.featureToggle'),
    },
    {
      icon: <MailOutlined className="navbar__menu-item-icon" />,
      key: paths.adminBanner,
      label: t('navbar.admin.banner'),
    },
    {
      icon: <ProfileOutlined className="navbar__menu-item-icon" />,
      key: paths.adminReleaseNotes,
      label: t('navbar.admin.releaseNotes'),
    },
    {
      icon: <UserOutlined className="navbar__menu-item-icon" />,
      key: paths.adminUserManagement,
      label: t('navbar.admin.userManagement'),
    },
  ]

  // Check if user has admin access (admin, fleet manager, or ship roles)
  // Feature flag: controls whether the vessel draft edit action is available
  const isDisplayAdminPageEnabled = useFeatureAccessControlEnabled(
    featureCodes.DISPLAY_ADMIN_PAGE
  )

  const hasAdminAccess =
    isDisplayAdminPageEnabled && userRole === userRoles.ADMIN

  const menuItems = [
    ...(userModule?.includes(MODULES.CARGOMATE)
      ? [
          {
            children:
              userRole === userRoles.SHIP
                ? shipSubMenuItems
                : nonShipSubMenuItems,
            key: 'port-call',
            label: (
              <span className="navbar__menu-trigger-circle">
                <span className="navbar__menu-trigger-label">
                  {t('navbar.portCall')}
                </span>
                <CaretDownFilled className="navbar__menu-trigger-icon" />
              </span>
            ),
          },
        ]
      : []),
    ...(userModule?.includes(MODULES.ROB)
      ? [
          {
            children: robSubMenuItems,
            key: 'rob',
            label: (
              <span className="navbar__menu-trigger-circle">
                <span className="navbar__menu-trigger-label">
                  {t('navbar.rob.rob')}
                </span>
                <CaretDownFilled className="navbar__menu-trigger-icon" />
              </span>
            ),
          },
        ]
      : []),
    ...(hasAdminAccess
      ? [
          {
            children: adminSubmenu,
            key: 'admin-screen',
            label: (
              <span className="navbar__menu-trigger-circle">
                <span className="navbar__menu-trigger-label">
                  {t('navbar.admin.admin')}
                </span>
                <CaretDownFilled className="navbar__menu-trigger-icon" />
              </span>
            ),
          },
        ]
      : []),
  ]

  const drawerMenuItems = [
    ...(userModule?.includes(MODULES.CARGOMATE)
      ? [
          {
            children:
              userRole === userRoles.SHIP
                ? shipSubMenuItems
                : nonShipSubMenuItems,
            key: 'port-call',
            label: t('navbar.portCall'),
          },
        ]
      : []),
    ...(userModule?.includes(MODULES.ROB)
      ? [
          {
            children: robSubMenuItems,
            key: 'rob',
            label: t('navbar.rob.rob'),
          },
        ]
      : []),
    ...(hasAdminAccess
      ? [
          {
            children: adminSubmenu,
            key: 'admin-screen',
            label: t('navbar.admin.admin'),
          },
        ]
      : []),
  ]

  const renderNavItems = () => (
    <Menu
      items={menuItems}
      mode="horizontal"
      onSelect={onMenuItemSelected}
      selectedKeys={[location.pathname]}
      theme="dark"
    />
  )

  const deviceType = useDeviceType()
  const navigateToWhatsNew = () => navigate(`${paths.whatIsNew}`)

  const isNotificationsButtonDisabled =
    location.pathname === paths.overview && deviceType === deviceTypes.DESKTOP

  const isMobile = deviceType !== deviceTypes.DESKTOP

  const renderIconButtons = () => (
    <div className="navbar__icons-container">
      <Tooltip placement="left" title={t('navbar.sendFeedback')}>
        <Button
          className="navbar__icon-button navbar__icon-button--feedback"
          icon={<MailOutlined />}
          onClick={openFeedbackModal}
        >
          {t('navbar.feedback')}
        </Button>
      </Tooltip>

      <Tooltip placement="left" title={t('navbar.whatsNew')}>
        <Dropdown
          placement="bottomRight"
          trigger={['click']}
          dropdownRender={() => (
            <WhatsNewDropdown
              onCancel={closeWhatsNewNotification}
              onSeeMore={navigateToWhatsNew}
              setNewsIdsSeen={setNewsIdsSeen}
              userRole={userRole}
            />
          )}
          open={
            shouldShowWhatsNewNotification &&
            !shouldShowEnableNotificationsPrompt
          }
        >
          <Button
            icon={<GiftOutlined />}
            onClick={navigateToWhatsNew}
            shape="circle"
            className={`navbar__icon-button ${
              hasUnseenNews ? 'navbar__icon-button--unseen-news' : ''
            }`}
          />
        </Dropdown>
      </Tooltip>

      {shouldShowPortCallNotifications && (
        <Tooltip
          placement="left"
          title={
            isNotificationsButtonDisabled
              ? t('header.alreadyOpen')
              : t('navbar.notifications')
          }
        >
          <div className="navbar__icon-button--notifications-container">
            <Button
              className="navbar__icon-button"
              disabled={isNotificationsButtonDisabled}
              icon={<BellOutlined />}
              onClick={onNotificationsButtonClicked}
              shape="circle"
            />
            {!isNotificationsButtonDisabled && (
              <Badge
                className="navbar__icon-button--badge"
                count={totalNoOfNewNotifications}
                style={{ backgroundColor: colors.brandOrange }}
              />
            )}
          </div>
        </Tooltip>
      )}
      <Tooltip placement="left" title={t('navbar.profile')}>
        <Dropdown
          trigger={['click']}
          dropdownRender={() => (
            <MenuContent
              isUserSSO={isUserSSO}
              signOut={() => signOut({ redirect: navigate })}
              signOutLabel={t('header.signOut')}
              signedInAsLabel={t('header.signedInAs')}
              signedInAsValue={signedInAs}
            />
          )}
        >
          <Button
            className="navbar__icon-button"
            icon={<UserOutlined />}
            shape="circle"
          />
        </Dropdown>
      </Tooltip>
    </div>
  )

  return (
    <div className="navbar__container">
      <div className="navbar__container-content">
        {isMobile ? (
          <>
            <Button
              className="navbar__hamburger-button"
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
            />
            <div className="cargomate-logo-container cargomate-logo-container--mobile">
              <img
                alt={t('global.cargoMateLogoDescription')}
                className="cargomate-logo cargomate-logo--mobile"
                onClick={goToHomePage}
                src={cargomateLogo}
              />
            </div>
            {renderIconButtons()}
          </>
        ) : (
          <>
            <div className="cargomate-logo-container">
              <img
                alt={t('global.cargoMateLogoDescription')}
                className="cargomate-logo"
                onClick={goToHomePage}
                src={cargomateLogo}
              />
            </div>
            {renderNavItems()}
            {renderIconButtons()}
          </>
        )}
      </div>

      {/* Mobile Drawer */}
      <Drawer
        className="navbar__drawer"
        closeIcon={<ArrowLeftOutlined />}
        open={drawerVisible}
        placement="left"
        width={256}
        onClose={() => {
          setDrawerVisible(false)
        }}
        title={
          <img
            alt={t('global.cargoMateLogoDescription')}
            className="navbar__drawer-logo"
            src={cargomateLogo}
            onClick={() => {
              goToHomePage()
              setDrawerVisible(false)
            }}
          />
        }
      >
        <Menu
          defaultOpenKeys={['port-call']}
          items={drawerMenuItems}
          mode="inline"
          onSelect={onMenuItemSelected}
          selectedKeys={[location.pathname]}
          theme="dark"
        />
      </Drawer>

      <EnableNotificationsPrompt
        onCancel={onCancelEnableNotificationsPrompt}
        onEnableNotifications={onConfirmEnableNotificationsPrompt}
        isVisible={
          shouldShowEnableNotificationsPrompt && shouldShowPortCallNotifications
        }
      />
      <Feedback />
    </div>
  )
}

Navbar.defaultProps = {
  cognitoGroup: null,
  hasUnseenNews: false,
  userImoNumber: null,
  userModule: null,
  userRole: null,
}

Navbar.propTypes = {
  closeWhatsNewNotification: PropTypes.func.isRequired,
  cognitoGroup: PropTypes.string,
  hasUnseenNews: PropTypes.bool,
  isUserSSO: PropTypes.bool.isRequired,
  onCancelEnableNotificationsPrompt: PropTypes.func.isRequired,
  onConfirmEnableNotificationsPrompt: PropTypes.func.isRequired,
  onNotificationsButtonClicked: PropTypes.func.isRequired,
  openFeedbackModal: PropTypes.func.isRequired,
  setNewsIdsSeen: PropTypes.func.isRequired,
  shouldShowEnableNotificationsPrompt: PropTypes.bool.isRequired,
  shouldShowPortCallNotifications: PropTypes.bool.isRequired,
  shouldShowWhatsNewNotification: PropTypes.bool.isRequired,
  signOut: PropTypes.func.isRequired,
  signedInAs: PropTypes.string.isRequired,
  totalNoOfNewNotifications: PropTypes.number.isRequired,
  userImoNumber: PropTypes.string,
  userModule: PropTypes.arrayOf(PropTypes.string),
  userRole: propTypes.userRole,
}

const mapDispatchToProps = {
  openFeedbackModal,
}

const mapStateToProps = state => ({
  cognitoGroup: selectors.cognitoGroupSelector(state),
  selectedNavbarItem: selectors.selectedNavbarItemSelector(state),
  totalNoOfNewNotifications: selectors.totalNoOfNewNotificationsSelector(state),
  userImoNumber: selectors.userImoNumberSelector(state),
  userRole: selectors.userRoleSelector(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(Navbar)
